import '../base/buttons/as-icon-button';
import './subcomps/as-home-button';
import './subcomps/as-session-set';
import './subcomps/as-view-set';
import './subcomps/as-state-set';
import '../aesthetic/fade-container';
import '../overlays/as-popout-button';
import '../text-and-lang/as-text';
import '../base/as-spinner';
import { hasEnabledAttribute } from '../wcUtil.ts';
import { commonAppData } from '../../store/store';
import { logOut, getUserData } from '../../logic/asApi';
import { devlog } from '../../logic/utils';
import { fullAppSuiteRootURL } from '../../data/constants.ts';

import styleText from './as-header.css?raw';

// Source - https://stackoverflow.com/a
// Posted by Harshal Patil
// Retrieved 2025-11-10, License - CC BY-SA 4.0

// Read SCSS file as a raw CSS text using Webpack/Rollup/Parcel

const sheet = new CSSStyleSheet();
sheet.replaceSync(styleText);






/**
 * Main header component that composes all section components
 */
export class TrHeader extends HTMLElement {
  // private readonly headerImgDir = new URL('assets/img/header/', import.meta.url).pathname ;
  private readonly headerImgDir = 'assets/img/header/'
  private cleanedTitle = 'App';
  private isSigningOut = false;
  private popoutObserver: MutationObserver | null = null;
  
  static get observedAttributes(): string[] {
    return ['app-title','no-session-icons','no-view-icons','no-state-icons','no-default-icons','show-lang-selector'];
  }

  private handleAuthUpdate = (event: CustomEvent) => {
    devlog('Auth update received:', event.detail);
    this.render();
  };

  private handleSignOut = async (event: Event) => {
    event.preventDefault();
    this.isSigningOut = true;
    this.updateSignOutButton();
    try {
      await logOut();
    } catch (error) {
      console.error('Sign out failed:', error);
      this.isSigningOut = false;
      this.updateSignOutButton();
    }
  };

  private updateSignOutButton = () => {
    // Find the sign out link in the popout content
    const popoutButton = this.shadowRoot?.querySelector('#trincklePopout');
    if (popoutButton) {
      const signOutLink = popoutButton.querySelector('#signOutLink');
      if (signOutLink) {
        signOutLink.innerHTML = this.isSigningOut 
          ? '<as-spinner style="width: 1rem; height: 1rem;"></as-spinner>' 
          : '<as-text dict-key="signOutLink">Sign Out</as-text>';
      }
    }
  };

  private handleManageAccount = (event: Event) => {
    event.preventDefault();
    // Open the manage account modal in a new tab
    window.open(`${fullAppSuiteRootURL}/?modal=manage-user-view`, '_blank');
  };

  private generateUserOptionsPanel(): string {
    if (!commonAppData.userData) {
      // Show placeholders when data is loading
      return /*html*/`
        <!-- <link rel="stylesheet" href="./as-header.css"> -->
        <div id="userOptionsPanel" style="display: flex; flex-direction: column; gap: 1rem; min-width: 200px;">
          <span id="userGreeting" style="padding-bottom: 1rem;">
            Hello <span class="placeholder name"></span>
          </span>

          <span id="userEmail">
            <span class="placeholder email"></span>
          </span>

          <div style="display: flex; flex-direction: row;">
            <a id="manage-account-button" href="#" data-action="manage-account">
              <as-text dict-key="manageAccountLink">Manage Account</as-text>
            </a>
          </div>

          <a id="signOutLink" href="#" data-action="sign-out">
            <as-text dict-key="signOutLink">Sign Out</as-text>
          </a>
        </div>
      `;
    }

    const userData = commonAppData.userData;
    const userName = userData.firstName && userData.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : 'User';
    
    const organization = userData.organization ? `(${userData.organization})` : '';
    const email = userData.eMail || '';

    return /*html*/`
      <div id="userOptionsPanel" style="display: flex; flex-direction: column; gap: 1rem; min-width: 200px;">
        <span id="userGreeting" style="padding-bottom: 1rem;">
          Hello ${userName} ${organization}
        </span>

        ${email ? /*html*/`<span id="userEmail">${email}</span>` : ''}

        <div style="display: flex; flex-direction: row;">
          <a id="manage-account-button" href="#" data-action="manage-account">
            <as-text dict-key="manageAccountLink">Manage Account</as-text>
          </a>
        </div>

        <a id="signOutLink" href="#" data-action="sign-out">
          <as-text dict-key="signOutLink">Sign Out</as-text>
        </a>
      </div>
    `;
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'app-title') {
      const hasText = newValue.replaceAll(' ', '').length > 0;
      this.cleanedTitle = hasText ? newValue : 'App'
    }
    this.render();
  }
  render(){
    // Check if parent as-app has a clickthru-url
    const asApp = this.closest('as-app');
    const hasClickthruUrl = asApp?.hasAttribute('clickthru-url');

    this.shadowRoot!.innerHTML = /*html*/`
    <div class="header-content">
      <div class="header-left">
        <as-home-button></as-home-button>
        <as-text id="header-title" dict-key="${this.getAttribute('app.title')}">${  this.cleanedTitle }</as-text>
      </div>

      <slot name="slot-0"></slot>
      ${hasEnabledAttribute(this, 'no-session-icons') || hasEnabledAttribute(this, 'no-default-icons') ? '' : `<as-session-set></as-session-set>`}
      <slot name="slot-1"></slot>
      ${hasEnabledAttribute(this, 'no-view-icons') || hasEnabledAttribute(this, 'no-default-icons') ? '' : `<as-view-set></as-view-set>`}
      <slot name="slot-2"></slot>
      ${hasEnabledAttribute(this, 'no-state-icons') || hasEnabledAttribute(this, 'no-default-icons') ? '' : `<as-state-set></as-state-set>`}
      <slot name="slot-3"></slot>

      <div class="header-right">
        <!-- <as-popout-button>

        </as-popout-button> -->
        ${hasClickthruUrl ? `<as-icon-button
          id="header-help-button"
          name="header-help-button"
          tooltip-placement="bottom-left"
          class="default-header-button"
          tooltip-dict-key="header.help.tooltip"
          tooltip-default-text="Need help? Click here for a tutorial."
          tooltip-flash
          tooltip-flash-duration="3000"
          black

          icon-set="help"
          icon-variant="default"
          ></as-icon-button>` : ''}

        <as-popout-button    
          button-type="icon" 
          icon-name="trinckle-logo"
          file-name="header/logo-trinckle.png" 
          id="trincklePopout" 
          popout-placement="bottom-left"
          tooltip-dict-key="tooltip.userOptions"
          tooltip-default-text="User options"
          tooltip-placement="bottom-left"
        >
          ${this.generateUserOptionsPanel()}
        </as-popout-button>
        ${!hasEnabledAttribute(this, 'show-lang-selector') ? '' : `<as-lang-selector dict-key="langSelector"></as-lang-selector>`}
      </div>
    </div>
  `;
  }
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Listen for auth updates
    window.addEventListener('as-auth-update', this.handleAuthUpdate);
    
    this.render();
  }

  connectedCallback() {
    // Check for empty slots after the component is connected to the DOM
    this.setupSlotListeners();
    
    // Use MutationObserver to watch for popout content and attach event listeners
    this.setupPopoutObserver();
    // Use the sheet inside the web component constructor
    this.shadowRoot.adoptedStyleSheets = [sheet];

  }

  private setupPopoutObserver() {
    this.popoutObserver = new MutationObserver(() => {
      this.attachPopoutEventListeners();
    });

    // Start observing the shadow root for changes
    this.popoutObserver.observe(this.shadowRoot!, {
      childList: true,
      subtree: true
    });

    // Also try to attach listeners immediately in case content is already there
    setTimeout(() => this.attachPopoutEventListeners(), 100);
  }

  private attachPopoutEventListeners() {
    const popoutButton = this.shadowRoot?.querySelector('#trincklePopout');
    if (popoutButton) {
      // Add click handler to update panel content when popout is opened
      if (!popoutButton.hasAttribute('data-click-listener-attached')) {
        popoutButton.setAttribute('data-click-listener-attached', 'true');
        popoutButton.addEventListener('click', () => {
          this.updateUserOptionsPanel();
        });
      }

      const signOutLink = popoutButton.querySelector('#signOutLink');
      const manageAccountButton = popoutButton.querySelector('#manage-account-button');

      if (signOutLink && !signOutLink.hasAttribute('data-listener-attached')) {
        signOutLink.setAttribute('data-listener-attached', 'true');
        signOutLink.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          devlog('Sign out clicked');
          this.handleSignOut(e);
        });
      }

      if (manageAccountButton && !manageAccountButton.hasAttribute('data-listener-attached')) {
        manageAccountButton.setAttribute('data-listener-attached', 'true');
        manageAccountButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          devlog('Manage account clicked');
          this.handleManageAccount(e);
        });
      }
    }
  }

  private updateUserOptionsPanel() {
    const popoutButton = this.shadowRoot?.querySelector('#trincklePopout');
    if (popoutButton) {
      // Update the innerHTML of the popout button with fresh user data
      popoutButton.innerHTML = this.generateUserOptionsPanel();
      
      // Re-attach event listeners since we just replaced the content
      setTimeout(() => this.attachPopoutEventListeners(), 50);
    }
  }

  /**
   * Sets up listeners for slot content changes to add/remove hidden class
   */
  private setupSlotListeners(): void {
    const slots = this.shadowRoot!.querySelectorAll('slot');
    slots.forEach(slot => {
      // Initial check
      this.checkSlotContent(slot);
      
      // Listen for changes
      slot.addEventListener('slotchange', () => {
        this.checkSlotContent(slot);
      });
    });
  }
  
  /**
   * Checks if a slot has assigned nodes and hides empty slots
   */
  private checkSlotContent(slot: HTMLSlotElement): void {
    // Check for any non-whitespace text or element nodes
    const hasContent = Array.from(slot.assignedNodes()).some(node => {
      if (node.nodeType === Node.ELEMENT_NODE) return true;
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) return true;
      return false;
    });
    
    // Toggle the empty-slot class based on content
    if (hasContent) {
      slot.classList.remove('empty-slot');
    } else {
      slot.classList.add('empty-slot');
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    window.removeEventListener('as-auth-update', this.handleAuthUpdate);
    
    // Disconnect MutationObserver
    if (this.popoutObserver) {
      this.popoutObserver.disconnect();
      this.popoutObserver = null;
    }
  }
}

customElements.define('as-header', TrHeader); 